import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Phone, Star, Hammer, PaintRoller, Layers, Ruler, Droplets, CloudOff, CheckCircle2 } from "lucide-react";
import { HOME } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "(705) 796-4025";
const PHONE_HREF = "tel:+17057964025";

const ICONS = {
  "drywall-repair": Hammer,
  "patching": PaintRoller,
  "texture-matching": Layers,
  "ceiling-repair": Ruler,
  "water-damage": Droplets,
  "popcorn-removal": CloudOff,
};

const CARD_PALETTE = [
  { bg: "var(--coral)", text: "var(--cream)", num: "var(--cream)" },
  { bg: "var(--teal)", text: "var(--cream)", num: "var(--mustard)" },
  { bg: "var(--mustard)", text: "var(--ink)", num: "var(--coral)" },
  { bg: "var(--cream-soft)", text: "var(--ink)", num: "var(--coral)" },
  { bg: "var(--ink)", text: "var(--cream)", num: "var(--coral)" },
  { bg: "var(--teal-deep)", text: "var(--cream)", num: "var(--mustard)" },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useSeo({
    title: "Drywall Repair in Wasaga Beach · Southern Georgian Bay Repair Expert",
    description:
      "Drywall Repair in Wasaga Beach & area — Smooth Finish is your Southern Georgian Bay Repair Expert. Hole patching, ceiling repair, water damage & popcorn removal. Call (705) 796-4025.",
    path: "/",
  });

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid={HOME.hero}>
      {/* HERO */}
      <section className="relative pt-8 pb-24 sm:pt-12 sm:pb-32 overflow-hidden">
        <div className="container-x relative">
          {/* floating shapes */}
          <div className="absolute -top-20 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--mustard)] opacity-60 blur-3xl pointer-events-none"/>
          <div className="absolute top-20 right-0 w-[300px] h-[300px] rounded-full bg-[var(--coral)] opacity-30 blur-3xl pointer-events-none"/>

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 relative z-10">
              <span className="chip">★ No Mess No Stress</span>
              <h1 className="display-hero mt-7 text-[var(--ink)]">
                Your Local Drywall<br/>
                Repair Experts
              </h1>
              <p className="mt-8 font-body text-lg sm:text-xl text-[var(--ink-soft)] max-w-xl font-medium">
                Fixing Holes, cracks, ceiling stains, popcorn texture repairs since 1987 — we fix it cleanly, and professionally like it was never there.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a href={PHONE_HREF} data-testid={HOME.heroCta} className="btn-primary">
                  <Phone size={18}/> Call Mike for a free estimate
                </a>
                <a href={PHONE_HREF} data-testid={HOME.heroCallCta} className="btn-outline">
                  {PHONE_DISPLAY}
                </a>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
                <Stat number="30" suffix="+ yrs" label="Of drywall finishing" />
                <Stat number="500" suffix="+" label="Homes repaired" />
                <Stat number="5.0" label="Avg. star rating" />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/3] max-w-[560px] mx-auto">
                {/* Teal backdrop */}
                <div className="absolute inset-0 rounded-[40px] bg-[var(--teal)] translate-x-4 translate-y-4"/>
                <div className="absolute inset-0 rounded-[36px] overflow-hidden photo-modern">
                  <img src="/hero-drywall.jpg" alt="Before and after: popcorn ceiling holes patched and finished smooth" className="!object-contain bg-[var(--cream-soft)]"/>
                </div>
                {/* badge */}
                <div className="absolute -bottom-8 -right-6 bg-[var(--coral)] text-[var(--cream)] w-28 h-28 rounded-full flex flex-col items-center justify-center text-center shadow-xl float-slow">
                  <div className="font-display text-2xl font-bold leading-none">FREE</div>
                  <div className="text-[0.65rem] uppercase tracking-widest mt-1">Quotes</div>
                </div>
                {/* small card */}
                <div className="absolute -top-8 -right-4 sm:-right-8 bg-[var(--ink)] text-[var(--cream)] px-5 py-4 rounded-2xl max-w-[220px] shadow-xl">
                  <div className="flex gap-0.5 mb-1.5">
                    {[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-[var(--mustard)] text-[var(--mustard)]"/>)}
                  </div>
                  <p className="text-sm font-medium leading-snug">"Couldn't even find where the hole used to be."</p>
                  <p className="text-[0.65rem] uppercase tracking-widest text-[#E8E1D2]/60 mt-2">Jenna · Wasaga Beach</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* marquee */}
        <div className="mt-24 overflow-hidden border-y border-[var(--line)] bg-[var(--cream-soft)]">
          <div className="marquee py-5 text-2xl font-display font-bold uppercase tracking-tight">
            {[..."Hole patching · Ceiling repair · Water damage · Popcorn removal · Smooth finish · ".repeat(2)].join("").split("·").filter(Boolean).map((t, i) => (
              <span key={i} className="flex items-center gap-12 text-[var(--ink)]">
                <span>{t.trim()}</span>
                <span className="w-3 h-3 rounded-full bg-[var(--coral)]"/>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid={HOME.servicesPreview} className="section pt-0">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="num-badge">— 01 / SERVICES</span>
              <h2 className="display-xl mt-4">Expert Drywall Repair.<br/>One smooth obsession.</h2>
            </div>
            <p className="text-lg text-[var(--muted)] max-w-md">
              Every repair is hand finished to perfection, until the damage completely disappears.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, idx) => {
              const Icon = ICONS[s.slug] || Hammer;
              const p = CARD_PALETTE[idx % CARD_PALETTE.length];
              return (
                <Link
                  key={s.slug}
                  to="/services"
                  data-testid={HOME.serviceCard(s.slug)}
                  className="card-modern p-8 flex flex-col justify-between min-h-[280px] relative overflow-hidden group"
                  style={{ background: p.bg, color: p.text, border: "1px solid rgba(15,26,26,0.08)" }}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center bg-black/10 group-hover:scale-110 transition-transform" style={{ color: p.text }}>
                      <Icon size={24}/>
                    </div>
                    <div className="font-display font-bold text-2xl" style={{ color: p.num }}>0{idx + 1}</div>
                  </div>
                  <div>
                    <h3 className="display-lg mt-10" style={{ color: p.text }}>{s.title}</h3>
                    <p className="mt-3 text-sm opacity-85 leading-relaxed" style={{ color: p.text }}>{s.summary}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS - dark band */}
      <section className="bg-[var(--ink)] text-[var(--cream)] py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" style={{ filter: "invert(1)" }}/>
        <div className="container-x relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="num-badge">— 02 / HOW WE WORK</span>
              <h2 className="display-xl mt-4">From <span className="text-[var(--coral)]">"oh no"</span> to <span className="text-[var(--mustard)]">"oh wow"</span>.</h2>
              <p className="mt-6 text-[#E8E1D2]/70 max-w-md text-lg">A no-surprises process designed to make you forget the wall was ever damaged.</p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {[
                ["01", "Free on-site quote", "We'll come directly to your door, assess the damage, and provide a clear estimate with zero pressure."],
                ["02", "Dust-controlled prep", "Plastic up, floors covered, vents sealed. Spotless work zone."],
                ["03", "Hand-finished repair", "Crafted by hand with expert precision, ensuring a flawlessly smooth, seamless finish that blends beautifully into any room."],
                ["04", "100% primer ready", "Expertly finished drywall repairs are now ready for primer to make your walls look brand new."],
              ].map(([n, t, b]) => (
                <div key={n} className="p-6 rounded-2xl border border-[#E8E1D2]/15 hover:border-[var(--coral)] transition-colors">
                  <div className="font-display font-bold text-5xl text-[var(--coral)] leading-none">{n}</div>
                  <h4 className="text-xl font-bold mt-4">{t}</h4>
                  <p className="text-sm text-[#E8E1D2]/70 mt-2 leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid={HOME.testimonialsSection} className="section">
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="num-badge">— 03 / KIND WORDS</span>
            <h2 className="display-xl mt-4">People love the way we finish.</h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => {
              const styles = [
                { bg: "var(--coral)", text: "var(--cream)" },
                { bg: "var(--cream-soft)", text: "var(--ink)" },
                { bg: "var(--teal)", text: "var(--cream)" },
                { bg: "var(--mustard)", text: "var(--ink)" },
                { bg: "var(--ink)", text: "var(--cream)" },
                { bg: "var(--cream-soft)", text: "var(--ink)" },
              ];
              const s = styles[i % styles.length];
              return (
                <div key={i} className="p-8 rounded-3xl flex flex-col h-full" style={{ background: s.bg, color: s.text }}>
                  <div className="flex gap-0.5 mb-5">
                    {[...Array(t.rating || 5)].map((_, k) => <Star key={k} size={16} className="fill-current opacity-90"/>)}
                  </div>
                  {t.title && (
                    <h3 className="font-display font-bold text-2xl leading-tight mb-3">{t.title}</h3>
                  )}
                  <p className={`${t.title ? "text-base font-body font-medium" : "text-xl font-display font-medium"} leading-snug flex-1`}>"{t.quote}"</p>
                  <div className="mt-6 pt-5 border-t border-current/15 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs opacity-70">{t.location}</div>
                    </div>
                    <div className="font-display font-bold text-3xl opacity-60">0{i + 1}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section data-testid={HOME.ctaBanner} className="section pt-0">
        <div className="container-x">
          <div className="relative rounded-[40px] overflow-hidden bg-[var(--coral)] text-[var(--cream)] px-8 sm:px-16 py-20 sm:py-28">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--mustard)] opacity-90"/>
            <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[var(--teal)] opacity-90"/>

            <div className="relative grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <span className="chip cream-on-dark">Ready when you are</span>
                <h2 className="display-xl mt-5 text-[var(--cream)]">
                  One Call<br/>One Estimate<br/><span className="text-[var(--ink)]">Fast Response.</span>
                </h2>
                <ul className="flex flex-wrap gap-x-7 gap-y-2 mt-8 text-[#E8E1D2]/95 text-sm font-semibold">
                  <li className="inline-flex items-center gap-2"><CheckCircle2 size={16}/> Reliability</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 size={16}/> Quality Workmanship</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 size={16}/> Stress free</li>
                  <li className="inline-flex items-center gap-2"><CheckCircle2 size={16}/> Transparent communication</li>
                </ul>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <a href={PHONE_HREF} className="btn-cream !text-lg !px-7 !py-4">
                  <Phone size={20}/> Call Mike for a free estimate
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ number, suffix = "", label }) {
  return (
    <div>
      <div className="font-display font-bold text-4xl sm:text-5xl text-[var(--ink)] leading-none flex items-baseline gap-1">
        {number}<span className="text-[var(--coral)] text-2xl sm:text-3xl">{suffix}</span>
      </div>
      <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[var(--muted)] mt-2 font-semibold">{label}</div>
    </div>
  );
}
